/* eslint-disable no-sequences */
import { useState, useEffect, useRef, useCallback, useMemo, type PropsWithChildren } from 'react';
import { useRouter } from 'next/router';
import { UpOutlined } from '@ant-design/icons';
import Headroom from 'react-headroom';
import dynamic from 'next/dynamic';

import { HeadSEO } from 'components/base/headSEO';
import Breadcrumb from 'components/breadcrumb';
import I18nModal from 'components/i18nModal';
import EventsTwsModal from 'components/eventsTwsModal';
import GiftBagModal from 'components/giftBagModal';
import { MyOrderModal, GlobalEOrdersWidget } from 'components/myOrderModal';
import { useI18n } from 'context/i18n';
import { useAppData } from 'context/app';

import { getLang, getMarket, getCookie, COOKIES, setCookie, getUnbxdTranslations } from 'utils/helpers';
import { dataLayerHandleEvent } from 'utils/dataLayers';

import { useIsMobile, useLocale } from 'hooks';

import SearchTNM from 'components/search';
import { getDomainValue } from 'utils/domain';

import { Button } from 'components/ui/button';
import { useEventListener } from 'hooks/useEventListener';
import type { NewsletterCmsData } from 'components/newsletterModal';
import { SearchBoxJson } from 'components/ld-json/searchLdJson';
import { LabSearchIcon, LabSearchMobileIcon } from 'components/camperlab/icons/search';

import styles from './style.module.css';

const Footer = getDomainValue({
  camper: dynamic(() => import('@camper/react-web-components').then((res) => res.ORGANISMS.Footer)),
  nnormal: dynamic(() => import('@nnormal/react-web-components').then((res) => res.ORGANISMS.Footer)),
  camperlab: dynamic(() => import('@camper/react-web-components').then((res) => res.ORGANISMS.Footer)),
});
const TnmComponent = getDomainValue({
  camper: dynamic(() => import('@camper/react-web-components').then((res) => res.ORGANISMS.TnmComponent)),
  nnormal: dynamic(() => import('@nnormal/react-web-components').then((res) => res.ORGANISMS.TnmComponent)),
  camperlab: dynamic(() => import('@camper/react-web-components').then((res) => res.ORGANISMS.TnmComponent)),
});
const Ribbon = dynamic(() => import('components/layout/ribbon').then((mod) => mod.Ribbon));
const MiniBag = dynamic(() => import('components/miniBag').then((mod) => mod.MiniBag));
const MiniBagCamperlab = dynamic(() => import('components/miniBag/camperlab').then((mod) => mod.MiniBagCamperlab));
const AccountMenu = dynamic(() => import('components/layout/account-menu').then((mod) => mod.AccountMenu));
const MobileAccountButton = dynamic(() => import('components/layout/mobile-account-button').then((mod) => mod.MobileAccountButton));
const MembersOnlyModal = dynamic(() => import('components/membersOnlyModal').then((mod) => mod.MembersOnlyModal), { ssr: false });
const StoreDataRow = dynamic(() => import('components/storeDataRow').then((mod) => mod.StoreDataRow));
const CamperLabMyOrders = dynamic(() => import('components/camperlab/camperlab-order-drawer').then((mod) => mod.CamperlabMyOrders));
const AbandonedCartBanner = dynamic(() => import('components/abandonedCartBanner').then((mod) => mod.AbandonedCartBanner));
const NewsletterModal = dynamic(() => import('components/newsletterModal').then((mod) => mod.NewsletterModal));
const LabSearchDrawer = dynamic(() => import('components/search/lab-search-drawer').then((mod) => mod.LabSearchDrawer));
const MyAccountModalV2 = dynamic(() => import('components/myAccount/my-account-modal').then((mod) => mod.MyAccountModal));

const widgetBagHidden = ['/compra'];

const myOrderEvents = {
  account: { event: 'main_menu', event_category: 'menu', event_detail_1: 'my account', event_detail_2: 'orders' },
  tracking: { event: 'footer_menu', event_category: 'menu', event_detail_1: 'shopping on website', event_detail_2: 'order tracking' },
};

function WithHeadRoom({ children, onPin, onUnpin }) {
  if (getDomainValue({ camper: false, nnormal: false, camperlab: true })) {
    return children;
  }
  return (
    <Headroom onPin={onPin} onUnpin={onUnpin}>
      {children}
    </Headroom>
  );
}

type LayoutProps = PropsWithChildren<{
  footerProps: any;
  withFooter?: boolean;
  withHead?: boolean;
  tnmProps: any;
  breadcrumbs?: boolean;
  breadcrumbsBottom?: boolean;
  breadcrumbI18n?: any;
  breadcrumbFragments?: any[];
  ribbon?: any[];
  metatags?: { pageTitle?: string; metaDescription?: string };
  camperlab?: boolean;
  scrollArrow?: boolean;
  idBag?: string;
  pageName?: string;
  translateSlug?: boolean;
  recycled?: boolean;
  gtmProps?: any;
  popupProps?: NewsletterCmsData;
}>;

export default function Layout({
  children,
  footerProps,
  withFooter = true,
  withHead = true,
  tnmProps,
  breadcrumbs = true,
  breadcrumbsBottom = false,
  breadcrumbI18n = {},
  breadcrumbFragments = [],
  ribbon = [],
  metatags = null,
  camperlab = false,
  scrollArrow = false,
  pageName = null,
  translateSlug = false,
  recycled = false,
  gtmProps,
  popupProps,
}: LayoutProps) {
  const { pathname } = useRouter();

  const { countries, t, profileData, appmode, storeData, i18nPage, setStoreData } = useI18n();
  const { newsletter } = useAppData();
  const isMobile = useIsMobile();
  const { locale, language, country } = useLocale();

  const { global_e, currency } = profileData;
  const [showingI18nModal, setShowingI18nModal] = useState(false);
  const [showingMyOrderModal, setShowingMyOrderModal] = useState(false);
  const [eventTWSId, setEventTWSId] = useState<string | null>(null);
  const [eventsTwsModalType, setEventsTwsModalType] = useState('subscribe');
  const [eventTwsEmail, setEventTwsEmail] = useState('');
  const [showingTnm, setShowingTnm] = useState(false);
  const [scrollArrowVisible, setScrollArrowVisible] = useState(false);
  const [userCS, setUserCS] = useState(null);
  const [showGiftBag, setShowGiftBag] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  const [showFooter, setShowFooter] = useState(withFooter);
  const [showHeader, setShowHeader] = useState(withHead);

  const market = getMarket(locale);

  const handleChangeOrdersVisibility = (value, origin = null) => {
    setShowingMyOrderModal(value);

    if (origin) {
      const event = myOrderEvents[origin];

      if (event) {
        dataLayerHandleEvent(event);
      }
    }
  };

  useEffect(() => {
    if (window !== undefined) {
      setUserCS(getCookie(COOKIES.CS_EMPLOYEE_ID));

      window.sendDataLayer = (eventPayload) => {
        dataLayerHandleEvent(eventPayload);
      };

      window.showGiftBagPopUp = () => {
        setShowGiftBag(true);
      };

      window.showEventsTwsModal = (id = '0') => {
        setEventTWSId(id);
      };
      window.showOrderPopUp = () => {
        setShowingMyOrderModal(true);
      };

      const urlParams = new URLSearchParams(window.location.search);
      const eventId = urlParams.get('eventId');
      const email = urlParams.get('email');

      if (eventId && email) {
        setEventTWSId(eventId);
        setEventsTwsModalType('unsubscribe');
        setEventTwsEmail(email);
      }

      if (eventId) {
        setEventTWSId(eventId);
      }

      if (global_e) {
        const [lang, country] = [getLang(locale), getMarket(locale)];
        const data = { countryISO: country, currencyCode: currency, cultureCode: lang };

        const GlobalE_Data = encodeURIComponent(JSON.stringify(data));

        setCookie(COOKIES.GLOBAL_E, GlobalE_Data, 1, COOKIES.DOMAIN);
      }
    }
  }, []);

  const handleOnPinNav = () => {
    const filterNav = document.querySelector<HTMLElement>('#plp-filter-nav');
    const isDesktop = !(window.innerWidth < 1200);
    const tnmSelector = `.tnm--${isDesktop ? 'desktop' : 'mobile'}`;
    if (filterNav) {
      const tnmHeight = document.querySelector<HTMLElement>(tnmSelector).offsetHeight;
      filterNav.style.top = `${tnmHeight}px`;
      const filterPanel = document.querySelector<HTMLElement>('#plp-filter-panel');
      (filterPanel.firstChild as HTMLElement).style.top = `${filterNav.offsetHeight + tnmHeight}px`;
      (filterPanel.firstChild as HTMLElement).style.maxHeight = `calc(100vh - ${filterNav.offsetHeight + tnmHeight}px)`;
    }
    const stickyCol = document.querySelector<HTMLElement>('#sticky-col');
    if (stickyCol) {
      const tnmHeight = document.querySelector<HTMLElement>(tnmSelector).offsetHeight;
      stickyCol.style.top = `${tnmHeight}px`;
    }
    const landingSticky = document.querySelector<HTMLElement>('#landing-sticky');
    if (landingSticky) {
      const tnmHeight = document.querySelector<HTMLElement>(tnmSelector).offsetHeight;
      landingSticky.style.top = `${tnmHeight}px`;
    }
  };
  const handleOnUnpinNav = () => {
    const filterNav = document.querySelector<HTMLElement>('#plp-filter-nav');
    if (filterNav) {
      filterNav.style.top = '0px';
      const filterPanel = document.querySelector<HTMLElement>('#plp-filter-panel');
      (filterPanel.firstChild as HTMLElement).style.top = `${filterNav.offsetHeight}px`;
      (filterPanel.firstChild as HTMLElement).style.maxHeight = `calc(100vh - ${filterNav.offsetHeight}px)`;
    }
    const stickyCol = document.querySelector<HTMLElement>('#sticky-col');
    if (stickyCol) {
      stickyCol.style.top = '0px';
    }
    const landingSticky = document.querySelector<HTMLElement>('#landing-sticky');
    if (landingSticky) {
      landingSticky.style.top = '0px';
    }
  };
  const onTnmShow = () => {
    if (window?.hideAutosuggest) {
      window.hideAutosuggest();
    }

    if (!showingTnm) {
      setShowingTnm(true);
    }
  };

  const onTnmHide = () => {
    setShowingTnm(false);
  };

  let ordersWidget = getDomainValue({
    camper: <MyOrderModal visible={showingMyOrderModal} onClose={() => setShowingMyOrderModal(false)} />,
    nnormal: <MyOrderModal visible={showingMyOrderModal} onClose={() => setShowingMyOrderModal(false)} />,
    camperlab: <CamperLabMyOrders visible={showingMyOrderModal} onClose={() => setShowingMyOrderModal(false)} />,
  });

  if (global_e) {
    ordersWidget = (
      <GlobalEOrdersWidget
        onOpen={() => setShowingMyOrderModal(true)}
        visible={showingMyOrderModal}
        onlyModal={true}
        onClose={() => setShowingMyOrderModal(false)}
      />
    );
  }

  const isCamperlabInCamper = getDomainValue({ camper: camperlab, nnormal: false, camperlab: false });

  let layoutStyle = undefined;
  if (recycled) {
    layoutStyle = { '--background': 'var(--color-recycled)' };
  } else if (isCamperlabInCamper) {
    layoutStyle = { '--background': 'var(--color-camperlab)', '--font-primary': 'var(--font-camperlab)' };
  }

  {
    /* <AccountMenu dark={isMobile ? false : camperlab} onClickYourOrders={() => handleChangeOrdersVisibility(true, 'account')} onClickSignIn={onClickSignIn} /> */
  }
  const accountWidget = getDomainValue({
    camper: <MobileAccountButton onPress={() => window.openAccountModal?.()} />,
    nnormal: (
      <MobileAccountButton onPress={() => window.openAccountModal?.()} />
      // <AccountMenu dark={isMobile ? false : camperlab} onClickYourOrders={() => handleChangeOrdersVisibility(true, 'account')} onClickSignIn={onClickSignIn} />
    ),
    // camperlab: <MyAccountWidgetCamperlab onClickYourOrders={() => handleChangeOrdersVisibility(true, 'account')} onClickSignIn={onClickSignIn} />,
    camperlab: (
      <Button
        variant="none"
        className="flex items-center text-lg font-bold uppercase lg:text-xs [&_span]:!py-0"
        decoration="underline"
        onPress={() => handleChangeOrdersVisibility(true, 'account')}
      >
        <span className="animated-underline inline-flex items-center whitespace-nowrap !py-0 after:hidden">{t('mi.cuenta', 'mis.pedidos', 'Orders')}</span>
      </Button>
    ),
  });

  const headerRef = useRef(null);
  const handleHeaderHeight = useCallback((node) => {
    // get node height and set it as a style variable --tnm-height
    if (node) {
      headerRef.current = node;
      const tnmHeight = node.offsetHeight;

      document.body.style.setProperty('--tnm-height', `${tnmHeight}px`);
    }
  }, []);

  const onWindowResize = useCallback(() => {
    const header = headerRef.current;

    if (header) {
      const tnmHeight = header.offsetHeight;
      document.body.style.setProperty('--tnm-height', `${tnmHeight}px`);
    }
  }, []);

  useEventListener('resize', onWindowResize);

  return (
    <div className={`${styles.layout}`} data-camperlab={isCamperlabInCamper ? 'true' : undefined} style={layoutStyle}>
      <StoreDataRow />
      <SearchBoxJson />
      <HeadSEO title={metatags?.pageTitle} description={metatags?.metaDescription} pageName={pageName} gtmProps={gtmProps} />
      <Ribbon
        items={ribbon}
        onClickNewsletter={() => {
          window.openNewsletterModal?.('header', { direct: true });
        }}
        accountWidget={<AccountMenu onClickYourOrders={() => handleChangeOrdersVisibility(true, 'account')} />}
        camperlab={getDomainValue({ camper: camperlab, nnormal: false, camperlab: true })}
      />

      {showHeader && (
        <header ref={handleHeaderHeight} className={`${styles.headerNav} lab:min-h-none lab:sticky lab:top-0 lab:z-30 lab:bg-neutral`}>
          {tnmProps && (
            <WithHeadRoom onPin={handleOnPinNav} onUnpin={handleOnUnpinNav}>
              <TnmComponent
                bagWidget={
                  !widgetBagHidden.includes(pathname) ?
                    getDomainValue({
                      camper: <MiniBag isInSalePath={pathname === '/bolsa'} />,
                      nnormal: <MiniBag isInSalePath={pathname === '/bolsa'} />,
                      camperlab: <MiniBagCamperlab isInSalePath={pathname === '/bolsa'} />,
                    })
                  : null
                }
                bagWidgetModal
                onTnmShow={onTnmShow}
                onTnmHide={onTnmHide}
                showSearch={true}
                searcher={getDomainValue({
                  camper: <SearchTNM />,
                  nnormal: <SearchTNM />,
                  camperlab: (
                    <Button
                      variant="none"
                      className="search-button flex items-center text-lg font-bold uppercase lg:text-xs [&_span]:!py-0"
                      onPress={() => setSearchOpen(true)}
                    >
                      <LabSearchMobileIcon className="lg:hidden" />
                      <LabSearchIcon className="max-lg:hidden" />
                      <span className="search-button__text animated-underline py-0 after:hidden max-lg:sr-only lg:inline-flex lg:items-center">
                        {t('search.widget', 'search', 'Search')}
                      </span>
                    </Button>
                  ),
                })}
                {...tnmProps}
                accountWidget={accountWidget}
                showI18nModal={() => setShowingI18nModal(true)}
                showNewsletterModal={() => {
                  window.openNewsletterModal?.('footer', { direct: true });
                }}
                showOrderTrackingModal={() => setShowingMyOrderModal(true)}
                isStoreMode={Boolean(getCookie(COOKIES.STORE_MODE))}
              />
              {getDomainValue({ camper: <AbandonedCartBanner /> })}
            </WithHeadRoom>
          )}
        </header>
      )}

      <div
        id="layoutContent"
        className={
          getDomainValue({ camper: showingTnm, nnormal: showingTnm, camperlab: false }) ?
            `${styles.backdrop} ${styles.backdropVisible}`
          : `${styles.backdrop} lab:mt-header`
        }
      >
        {breadcrumbs && !breadcrumbsBottom && <Breadcrumb locale={locale} i18nKeys={breadcrumbI18n} camperlab={camperlab} fragments={breadcrumbFragments} />}
        {children}
        {breadcrumbs && breadcrumbsBottom && (
          <div className={styles.breadcrumbsBottom}>
            <Breadcrumb locale={locale} i18nKeys={breadcrumbI18n} camperlab={camperlab} fragments={breadcrumbFragments} pageName={pageName} />
          </div>
        )}
        {showFooter ?
          <>
            {footerProps ?
              <Footer
                {...footerProps}
                showI18nModal={() => setShowingI18nModal(true)}
                showNewsletterModal={() => {
                  window.openNewsletterModal?.('footer', { direct: true });
                }}
                profileData={profileData}
                showOrderTrackingModal={() => {
                  handleChangeOrdersVisibility(true, 'tracking');
                }}
                newsletterTitle={newsletter?.title ?? ''}
                newsletterDescription={newsletter?.text ?? ''}
              />
            : null}
          </>
        : null}
        {getDomainValue({ camper: true, nnormal: true, camperlab: false }) && scrollArrow && (
          <button
            className={`${styles.scrollArrow} ${scrollArrowVisible ? '' : 'escondido'} data-[visible=desktop]:max-md:hidden data-[visible=mobile]:md:hidden`}
            data-visible={typeof scrollArrow === 'string' ? scrollArrow : undefined}
            onClick={() => {
              if (window && window.document) {
                const [body] = document.getElementsByTagName('body');

                if (body) {
                  body.scrollIntoView({ behavior: 'smooth' });
                }
              }
            }}
          >
            <UpOutlined />
          </button>
        )}

        {ordersWidget}
      </div>
      <MyAccountModalV2 />
      <GiftBagModal visible={showGiftBag} onClose={() => setShowGiftBag(false)} />
      <I18nModal countries={countries} visible={showingI18nModal} changeVisibility={setShowingI18nModal} translateSlug={translateSlug} />
      <EventsTwsModal
        loading={false}
        id={eventTWSId === null ? '0' : eventTWSId}
        modalTwsVisible={eventTWSId !== null}
        onClose={() => setEventTWSId(null)}
        type={eventsTwsModalType}
        email={eventTwsEmail}
      />
      <NewsletterModal cmsData={popupProps} camperlab={camperlab} />
      <MembersOnlyModal camperlab={camperlab} />
      {getDomainValue({ camperlab: <LabSearchDrawer isOpen={searchOpen} setIsOpen={setSearchOpen} /> })}
    </div>
  );
}
